import { DIcon } from "@dfa/ui-platform-icons";
import { sanitizeHTML } from "@dfa/ui-platform-react";
import { eventMisfired } from "components/public-site/audience-sort/utility/audienceSortApiHandlers";
import { setCookie, getCookie } from "components/utility/cookies/CookiesDIM";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type StickyPromoBannerProps = {
	data: {
		imageSrc?: string;
		imageAlt?: string;
		description?: string;
		cta?: {
			linkTarget: string;
			linkURL: string;
			linkText: string;
		};
		backgroundImageSrc?: string;
		backgroundImageAlt?: string;
		backgroundColor?: string;
		theme?: string;
		cookieName?: string;
		cookieExpiryDays?: string;
		analyticsGroup?: string;
		hasCloseButton?: boolean;
	};
};

const StickyPromoBanner = ({ data }: React.PropsWithChildren<StickyPromoBannerProps>) => {
	const {
		imageSrc,
		imageAlt,
		description,
		backgroundImageSrc,
		cta,
		backgroundImageAlt,
		backgroundColor,
		theme = "dark",
		cookieName,
		cookieExpiryDays,
		analyticsGroup,
		hasCloseButton = true
	} = data || {};

	const { linkTarget, linkURL, linkText } = cta || {};

	const className = "sticky-promo-banner";

	let shouldShowBanner = false;

	if (cookieName) {
		const cookie = getCookie(cookieName);
		shouldShowBanner = !cookie;
	}

	const [isXTopAligned, setIsXTopAligned] = useState<boolean>(false);
	const [isBannerVisible, setIsBannerVisible] = useState<boolean>(shouldShowBanner);

	const topAlignTrigger = 100; // X Moves to top when content height >= 100px

	// refs
	const containerRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);

	// Callbacks
	const handleCloseButton = useCallback(
		(event: React.MouseEvent | React.KeyboardEvent) => {
			if (eventMisfired(event)) return;

			const stickyBannerContainer = containerRef.current;
			const stickyBanner = contentRef.current;

			if (stickyBannerContainer && stickyBanner) {
				stickyBannerContainer.ontransitionend = () => {
					if (cookieName) setCookie(cookieName, true, cookieExpiryDays);
					setIsBannerVisible(false);
				};

				const stickyBannerHeight = stickyBanner.offsetHeight;
				stickyBannerContainer.style.transition = "height .3s linear";
				const stickyHeader = document.querySelector(`.sticky-header:has(.${className})`);

				if (stickyHeader) {
					const stickyHeaderContent =
						stickyHeader.querySelector<HTMLDivElement>(".sticky-header-el");
					const stickyHeaderPlaceHolder = stickyHeader.querySelector<HTMLDivElement>(
						".sticky-header-placeholder"
					);

					if (stickyHeaderPlaceHolder && stickyHeaderContent) {
						stickyHeaderPlaceHolder.style.height = `${
							stickyHeaderContent.offsetHeight - stickyBannerHeight
						}px`;
					}
				}

				stickyBannerContainer.style.height = "0";
			}
		},
		[cookieExpiryDays, cookieName]
	);

	const handleResize = useCallback(() => {
		const stickyBannerContainer = containerRef.current;
		const stickyBanner = contentRef.current;

		if (stickyBannerContainer && stickyBanner) {
			stickyBannerContainer.style.height = `${stickyBanner.offsetHeight / 16}rem`;
			setIsXTopAligned(stickyBannerContainer.scrollHeight > topAlignTrigger);
		}
	}, []);

	// useEffects
	useEffect(() => {
		handleResize();

		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, [handleResize]);

	// Memoized elements
	const bannerStyle = useMemo(
		() => ({ alignItems: isXTopAligned ? "flex-start" : "center" }),
		[isXTopAligned]
	);

	const BackgroundImage = backgroundImageSrc ? (
		<div className={`${className}-background-image`}>
			<img src={backgroundImageSrc} alt={backgroundImageAlt} />
		</div>
	) : null;

	const BannerImage = imageSrc ? (
		<div className={`${className}-content-image`}>
			<img src={imageSrc} alt={imageAlt} onLoad={handleResize} />
		</div>
	) : null;

	const Description = description ? (
		<div
			className={`${className}-content-text-description`}
			dangerouslySetInnerHTML={{ __html: sanitizeHTML(description) }}
		/>
	) : null;

	const CTA = cta ? (
		<a
			className={`${className}-content-text-cta t-body-xs-heavy`}
			target={linkTarget}
			href={linkURL}
			data-a-evt="Link Click"
			data-a-comp="Sticky Promo Banner"
			data-a-name={linkText}
			data-a-group={analyticsGroup}
		>
			{linkText}
		</a>
	) : null;

	const CloseButton = hasCloseButton ? (
		<button
			className={`${className}-close`}
			onKeyDown={handleCloseButton}
			onClick={handleCloseButton}
			data-a-evt="Link Click"
			data-a-comp="Sticky Promo Banner"
			data-a-name="Close"
			data-a-group={analyticsGroup}
		>
			<DIcon icon="close" />
		</button>
	) : null;

	const TextContent =
		CTA || Description ? (
			<div className={`${className}-content-text`}>
				{Description}
				{CTA}
			</div>
		) : null;

	const Banner = (
		<div
			className={`${className}-container ${theme}`}
			style={{ backgroundColor }}
			ref={containerRef}
		>
			<div className={className} ref={contentRef} style={bannerStyle} data-a-group={analyticsGroup}>
				{BackgroundImage}
				<div className={`${className}-content`}>
					{BannerImage}
					{TextContent}
				</div>
				{CloseButton}
			</div>
		</div>
	);

	return isBannerVisible && data ? Banner : null;
};

export default StickyPromoBanner;
